import { useCallback } from 'react';

import { membershipSignupUrl } from '#src/lib/url-builders/membership-url';
import { logger } from '#src/lib/logger';

import { useRedirect } from './use-redirect';

/**
 * Returns a function that redirects to the membership signup page with the same order item inputs, thus recreating the original order.
 * This is useful when a user lands on a checkout page with an order not associated with their current user/session, as when opening a
 * checkout url in a different browser. This can occur when a user completes the quiz in an in-app browser (like Instagram) and then opens
 * the checkout url in a different browser (like Chrome).
 *
 * @param {object} orderItemInputs - The parameters used to create the original order, e.g. { offerId, promoCode, paymentPlan, gameType, gameId }
 * @param {string|number} [orderId] - The id of the original order; only used for logging; may be omitted
 * @returns {() => void} - A function that redirects to the membership signup page with the same order item inputs, thus recreating the original order
 */
export function useRecreateOrder(orderItemInputs, orderId) {
  const redirect = useRedirect();
  const recreateOrder = useCallback(() => {
    logger.info(
      `Recreating order (${orderId}) with inputs: ${JSON.stringify(
        orderItemInputs
      )}`
    );
    redirect(membershipSignupUrl(orderItemInputs));
  }, [orderItemInputs, orderId, redirect]);
  return recreateOrder;
}
